import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullseye,
  faChartLine,
  faCircleExclamation,
  faCoins,
  faHandHoldingDollar,
  faPiggyBank,
  faRotateRight,
  faShieldHalved,
  faWallet,
  faTriangleExclamation,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import swal from 'sweetalert';
import { db } from '../../firebase';
import '../dash.css';
import './financeReports.css';
import { normalizeRequisitionStatus } from './requisitionHelpers';
import {
  formatKes,
  getMonthKey,
  getMonthLabel,
  normalizeAmount,
  resolveBillPaymentPeriodKey,
  resolveRequisitionPaymentPeriodKey,
  resolveSalaryPaymentPeriodKey,
  filterSchoolFeeIncomeRecords,
  summarizeFixedBills,
  summarizePayroll,
  summarizeRequisitions,
  toDateValue
} from '../../utils/financeWorkflow';

const FINANCE_SUCCESS_STATUSES = new Set([
  'paid',
  'processed',
  'completed',
  'successful',
  'success',
  'succeeded',
  'approved'
]);

const normalizeStatus = (value) => String(value || '').trim().toLowerCase();

const isSuccessfulFinancePayment = (payment = {}) => {
  return FINANCE_SUCCESS_STATUSES.has(
    normalizeStatus(payment.paymentStatus || payment.status || payment.lotusTransactionStatus)
  );
};

const buildRecentMonthKeys = (monthKey, count = 12) => {
  const [year, month] = String(monthKey || getMonthKey()).split('-').map(Number);
  const seedDate = new Date(year || new Date().getFullYear(), (month || 1) - 1, 1);

  return Array.from({ length: count }, (_, index) => {
    const nextDate = new Date(seedDate.getFullYear(), seedDate.getMonth() - (count - 1 - index), 1);
    return getMonthKey(nextDate);
  });
};

const getMonthShortLabel = (monthKey) => {
  const [year, month] = String(monthKey || '').split('-').map(Number);
  const parsed = new Date(year, (month || 1) - 1, 1);

  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return parsed.toLocaleDateString('en-KE', { month: 'short' });
};

const getYearFromMonthKey = (monthKey) => {
  const [year] = String(monthKey || '').split('-').map(Number);
  return year || new Date().getFullYear();
};

const formatCompactKes = (value) => {
  const amount = normalizeAmount(value);

  if (amount >= 1000000) {
    return `KES ${(amount / 1000000).toFixed(1)}M`;
  }

  if (amount >= 1000) {
    return `KES ${(amount / 1000).toFixed(1)}K`;
  }

  return formatKes(amount);
};

const chartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const monthLabel = payload[0]?.payload?.monthLabel || label;

  return (
    <div className="financeReportsTooltip">
      <strong>{monthLabel}</strong>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="financeReportsTooltipRow">
          <span>{entry.name}</span>
          <strong>{formatKes(entry.value)}</strong>
        </div>
      ))}
    </div>
  );
};

function FinReportsComp() {
  const [loading, setLoading] = useState(true);
  const [periodKey, setPeriodKey] = useState(getMonthKey());
  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [income, setIncome] = useState([]);
  const [admissionFees, setAdmissionFees] = useState([]);
  const [otherIncome, setOtherIncome] = useState([]);
  const [bills, setBills] = useState([]);
  const [billPayments, setBillPayments] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [requisitionPayments, setRequisitionPayments] = useState([]);
  const [marketingExpenses, setMarketingExpenses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [salaryPayments, setSalaryPayments] = useState([]);
  const [legacyExpenses, setLegacyExpenses] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [liabilityPayments, setLiabilityPayments] = useState([]);
  const [statutoryRemittances, setStatutoryRemittances] = useState([]);
  const [statutoryRemittancePayments, setStatutoryRemittancePayments] = useState([]);

  const loadReports = async () => {
    const [
      vendorSnapshot,
      branchSnapshot,
      incomeSnapshot,
      admissionSnapshot,
      otherIncomeSnapshot,
      billSnapshot,
      billPaymentSnapshot,
      requisitionSnapshot,
      requisitionPaymentSnapshot,
      marketingExpenseSnapshot,
      employeeSnapshot,
      salaryPaymentSnapshot,
      expenseSnapshot,
      liabilitySnapshot,
      liabilityPaymentSnapshot,
      statutoryRemittanceSnapshot,
      statutoryRemittancePaymentSnapshot
    ] = await Promise.all([
      getDocs(collection(db, 'Vendors')),
      getDocs(collection(db, 'Branches')),
      getDocs(collection(db, 'Income')),
      getDocs(collection(db, 'AdmissionFeePayments')),
      getDocs(collection(db, 'otherIncome')),
      getDocs(collection(db, 'Bills')),
      getDocs(collection(db, 'BillPayments')),
      getDocs(collection(db, 'Requisitions')),
      getDocs(collection(db, 'RequisitionPayments')),
      getDocs(collection(db, 'marketingExpenses')),
      getDocs(collection(db, 'Employees')),
      getDocs(collection(db, 'SalaryPayments')),
      getDocs(collection(db, 'Expenses')),
      getDocs(collection(db, 'Loans')),
      getDocs(collection(db, 'LoanPayments')),
      getDocs(collection(db, 'StatutoryRemittances')),
      getDocs(collection(db, 'StatutoryRemittancePayments'))
    ]);

    setVendors(vendorSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setBranches(branchSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setIncome(incomeSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setAdmissionFees(admissionSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setOtherIncome(otherIncomeSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setBills(billSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setBillPayments(billPaymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setRequisitions(requisitionSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setRequisitionPayments(requisitionPaymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setMarketingExpenses(marketingExpenseSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setEmployees(employeeSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setSalaryPayments(salaryPaymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setLegacyExpenses(expenseSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setLiabilities(liabilitySnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setLiabilityPayments(liabilityPaymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setStatutoryRemittances(statutoryRemittanceSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setStatutoryRemittancePayments(
      statutoryRemittancePaymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() }))
    );
  };

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        await loadReports();
      } catch (error) {
        console.error('Failed to load finance reports:', error);
        swal('Error', 'Finance reports could not be loaded right now.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, []);

  const vendorsById = useMemo(() => {
    return vendors.reduce((accumulator, vendor) => {
      accumulator[vendor.id] = vendor;
      return accumulator;
    }, {});
  }, [vendors]);

  const branchMap = useMemo(() => {
    return branches.reduce((accumulator, branch) => {
      accumulator[branch.id] = branch.branchName;
      return accumulator;
    }, {});
  }, [branches]);

  const schoolFeeIncome = useMemo(() => filterSchoolFeeIncomeRecords(income), [income]);

  const billRows = useMemo(() => {
    return summarizeFixedBills({
      bills,
      vendorsById,
      branchesById: branchMap,
      payments: billPayments,
      periodKey
    });
  }, [billPayments, bills, branchMap, periodKey, vendorsById]);

  const requisitionRows = useMemo(() => {
    return summarizeRequisitions({
      requisitions,
      branchesById: branchMap,
      payments: requisitionPayments
    }).filter((item) => {
      const normalizedStatus = normalizeRequisitionStatus(item.status);
      return normalizedStatus === 'Approved' || normalizedStatus === 'Partially Approved';
    });
  }, [branchMap, requisitionPayments, requisitions]);

  const payrollRows = useMemo(() => {
    return summarizePayroll({
      employees,
      payments: salaryPayments,
      periodKey
    }).filter((employee) => String(employee.employeeStatus || 'Active').trim() !== 'Archived');
  }, [employees, periodKey, salaryPayments]);

  const liabilityRows = useMemo(() => {
    return liabilities
      .map((liability) => {
        const liabilityPaymentsForItem = liabilityPayments
          .filter((payment) => {
            const paymentLiabilityId = String(payment.liabilityId || payment.loanId || payment.debtId || '').trim();
            return paymentLiabilityId === liability.id;
          })
          .sort((first, second) => {
            const firstTime = toDateValue(first.paymentDate || first.createdAt)?.getTime() || 0;
            const secondTime = toDateValue(second.paymentDate || second.createdAt)?.getTime() || 0;
            return secondTime - firstTime;
          });

        const successfulPayments = liabilityPaymentsForItem.filter((payment) => isSuccessfulFinancePayment(payment));
        const principalAmount = normalizeAmount(
          liability.principalAmount || liability.loanAmount || liability.amount || 0
        );
        const totalPaidAmount = successfulPayments.reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
        const remainingAmount = Math.max(principalAmount - totalPaidAmount, 0);
        const dueDate = toDateValue(liability.dueDate);
        const overdue = remainingAmount > 0 && Boolean(dueDate) && dueDate < new Date();
        const dueSoon = remainingAmount > 0 && Boolean(dueDate) && dueDate <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

        return {
          id: liability.id,
          title: liability.liabilityTitle || liability.loanDesc || 'Liability',
          counterparty: liability.counterpartyName || liability.personWeOwe || liability.lenderName || 'Counterparty',
          type: liability.liabilityType || liability.category || 'Other',
          branchName: liability.selectedCampus
            ? branchMap[liability.selectedCampus] || (liability.selectedCampus === 'General' ? 'General' : 'Campus not set')
            : 'Campus not set',
          principalAmount,
          totalPaidAmount,
          remainingAmount,
          dueDate,
          overdue,
          dueSoon,
          paymentCount: successfulPayments.length,
          paymentHistory: liabilityPaymentsForItem
        };
      })
      .sort((first, second) => second.remainingAmount - first.remainingAmount);
  }, [branchMap, liabilities, liabilityPayments]);

  const statutoryRows = useMemo(() => {
    return statutoryRemittances
      .map((remittance) => {
        const remittancePayments = statutoryRemittancePayments
          .filter((payment) => String(payment.remittanceId || '').trim() === remittance.id)
          .sort((first, second) => {
            const firstTime = toDateValue(first.paymentDate || first.createdAt)?.getTime() || 0;
            const secondTime = toDateValue(second.paymentDate || second.createdAt)?.getTime() || 0;
            return secondTime - firstTime;
          });

        const successfulPayments = remittancePayments.filter((payment) => isSuccessfulFinancePayment(payment));
        const expectedAmount = normalizeAmount(remittance.amount || remittance.expectedAmount);
        const paidAmount = successfulPayments.reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
        const remainingAmount = Math.max(expectedAmount - paidAmount, 0);
        const dueDate = toDateValue(remittance.dueDate);
        const period = String(remittance.periodKey || getMonthKey(dueDate || remittance.createdAt)).trim();
        const overdue = Boolean(dueDate) && remainingAmount > 0 && dueDate.getTime() < Date.now();
        const paymentStatus = expectedAmount <= 0
          ? 'Amount not set'
          : remainingAmount <= 0
            ? 'Paid'
            : paidAmount > 0
              ? 'Partially paid'
              : overdue
                ? 'Overdue'
                : 'Pending';

        return {
          id: remittance.id,
          remittanceType: remittance.remittanceType || 'Statutory',
          periodKey: period,
          periodLabel: remittance.periodLabel || getMonthLabel(period),
          recipientName: remittance.recipientName || '',
          expectedAmount,
          paidAmount,
          remainingAmount,
          dueDate,
          overdue,
          paymentStatus,
          paymentCount: remittancePayments.length,
          successfulPaymentCount: successfulPayments.length
        };
      })
      .sort((first, second) => {
        const firstTime = toDateValue(first.dueDate || first.periodKey)?.getTime() || 0;
        const secondTime = toDateValue(second.dueDate || second.periodKey)?.getTime() || 0;
        return secondTime - firstTime;
      });
  }, [statutoryRemittancePayments, statutoryRemittances]);

  const currentMonthLabel = getMonthLabel(periodKey);
  const selectedYear = getYearFromMonthKey(periodKey);
  const yearMonthKeys = useMemo(() => buildRecentMonthKeys(`${selectedYear}-12`, 12), [selectedYear]);

  const currentMonthIncome = useMemo(() => {
    const schoolFees = schoolFeeIncome
      .filter((item) => getMonthKey(item.paymentDate || item.timeCreated) === periodKey)
      .reduce((sum, item) => sum + normalizeAmount(item.incomeAmount || item.amount), 0);
    const admission = admissionFees
      .filter((item) => getMonthKey(item.paymentDate || item.createdAt) === periodKey)
      .reduce((sum, item) => sum + normalizeAmount(item.amount || item.admissionFeeAmount), 0);
    const other = otherIncome
      .filter((item) => getMonthKey(item.dateReceived || item.timeCreated) === periodKey)
      .reduce((sum, item) => sum + normalizeAmount(item.amount), 0);

    return {
      schoolFees,
      admission,
      other,
      total: schoolFees + admission + other
    };
  }, [admissionFees, otherIncome, periodKey, schoolFeeIncome]);

  const currentMonthExpectedBills = useMemo(() => {
    return billRows.reduce((sum, bill) => sum + normalizeAmount(bill.expectedAmount), 0);
  }, [billRows]);

  const currentMonthExpectedPayroll = useMemo(() => {
    return payrollRows.reduce((sum, employee) => sum + normalizeAmount(employee.expectedAmount), 0);
  }, [payrollRows]);

  const currentMonthExpectedStatutory = useMemo(() => {
    return statutoryRows
      .filter((remittance) => remittance.periodKey === periodKey)
      .reduce((sum, remittance) => sum + normalizeAmount(remittance.expectedAmount), 0);
  }, [periodKey, statutoryRows]);

  const currentMonthRecurringExpectedTotal = currentMonthExpectedBills
    + currentMonthExpectedPayroll
    + currentMonthExpectedStatutory;

  const currentMonthMarketingExpenses = useMemo(() => {
    return marketingExpenses
      .filter((item) => getMonthKey(item.expenseDate || item.createdAt || item.updatedAt) === periodKey)
      .reduce((sum, item) => sum + normalizeAmount(item.expenseAmount || item.amount), 0);
  }, [marketingExpenses, periodKey]);

  const currentMonthStatutoryPaid = useMemo(() => {
    return statutoryRows
      .filter((remittance) => remittance.periodKey === periodKey)
      .reduce((sum, remittance) => sum + normalizeAmount(remittance.paidAmount), 0);
  }, [periodKey, statutoryRows]);

  const liabilitySummary = useMemo(() => {
    return {
      fixedBills: billRows.reduce((sum, bill) => sum + normalizeAmount(bill.remainingAmount), 0),
      requisitions: requisitionRows.reduce((sum, item) => sum + normalizeAmount(item.remainingAmount), 0),
      payroll: payrollRows.reduce((sum, employee) => sum + normalizeAmount(employee.remainingAmount), 0),
      statutory: statutoryRows.reduce((sum, item) => sum + normalizeAmount(item.remainingAmount), 0),
      loans: liabilityRows.reduce((sum, item) => sum + normalizeAmount(item.remainingAmount), 0)
    };
  }, [billRows, payrollRows, requisitionRows, liabilityRows, statutoryRows]);

  const totalExposure = liabilitySummary.fixedBills
    + liabilitySummary.requisitions
    + liabilitySummary.payroll
    + liabilitySummary.statutory
    + liabilitySummary.loans;

  const confirmedOutflowThisMonth = useMemo(() => {
    const fixedBills = billPayments
      .filter((payment) => isSuccessfulFinancePayment(payment))
      .filter((payment) => resolveBillPaymentPeriodKey(payment) === periodKey)
      .reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);

    const requisitions = requisitionPayments
      .filter((payment) => resolveRequisitionPaymentPeriodKey(payment) === periodKey)
      .reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);

    const statutory = statutoryRows
      .filter((remittance) => remittance.periodKey === periodKey)
      .reduce((sum, remittance) => sum + normalizeAmount(remittance.paidAmount), 0);

    const marketing = marketingExpenses
      .filter((item) => getMonthKey(item.expenseDate || item.createdAt || item.updatedAt) === periodKey)
      .reduce((sum, item) => sum + normalizeAmount(item.expenseAmount || item.amount), 0);

    const payroll = salaryPayments
      .filter((payment) => isSuccessfulFinancePayment(payment))
      .filter((payment) => resolveSalaryPaymentPeriodKey(payment) === periodKey)
      .reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);

    const liabilitiesPaid = liabilityPayments
      .filter((payment) => isSuccessfulFinancePayment(payment))
      .filter((payment) => getMonthKey(payment.paymentDate || payment.createdAt) === periodKey)
      .reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);

    const legacy = legacyExpenses
      .filter((expense) => getMonthKey(expense.expenseDate || expense.timeStamp) === periodKey)
      .reduce((sum, expense) => sum + normalizeAmount(expense.expenseAmount), 0);

    return {
      fixedBills,
      requisitions,
      statutory,
      marketing,
      payroll,
      liabilities: liabilitiesPaid,
      legacy,
      total: fixedBills + requisitions + statutory + marketing + payroll + liabilitiesPaid + legacy
    };
  }, [
    billPayments,
    legacyExpenses,
    liabilityPayments,
    marketingExpenses,
    periodKey,
    requisitionPayments,
    salaryPayments,
    statutoryRows
  ]);

  const currentMonthRecurringPaidTotal = confirmedOutflowThisMonth.fixedBills
    + confirmedOutflowThisMonth.payroll
    + currentMonthStatutoryPaid;

  const currentMonthCurrentExpenseTotal = confirmedOutflowThisMonth.marketing
    + confirmedOutflowThisMonth.requisitions
    + confirmedOutflowThisMonth.liabilities;

  const currentMonthRecurringOutstanding = Math.max(
    currentMonthRecurringExpectedTotal - currentMonthRecurringPaidTotal,
    0
  );

  const currentMonthActualOutflow = currentMonthRecurringPaidTotal + currentMonthCurrentExpenseTotal;
  const currentMonthNetPosition = currentMonthIncome.total - currentMonthActualOutflow;

  const fixedRecurringBreakdown = useMemo(() => ([
    {
      label: 'Fixed bills',
      note: 'Monthly recurring operating bills and vendor commitments.',
      amount: currentMonthExpectedBills
    },
    {
      label: 'Payroll',
      note: 'Expected salary run for active employees this month.',
      amount: currentMonthExpectedPayroll
    },
    {
      label: 'Statutory remittances',
      note: 'PAYE, NSSF, SHA, and other legal obligations due in the selected month.',
      amount: currentMonthExpectedStatutory
    }
  ]), [currentMonthExpectedBills, currentMonthExpectedPayroll, currentMonthExpectedStatutory]);

  const currentExpenseBreakdown = useMemo(() => ([
    {
      label: 'Marketing expenses',
      note: 'Current month marketing and growth spend already recorded.',
      amount: currentMonthMarketingExpenses
    },
    {
      label: 'Requisitions',
      note: 'Approved operational requests paid this month.',
      amount: confirmedOutflowThisMonth.requisitions
    },
    {
      label: 'Liability repayments',
      note: 'Debt service and other liability payments posted this month.',
      amount: confirmedOutflowThisMonth.liabilities
    }
  ]), [confirmedOutflowThisMonth, currentMonthMarketingExpenses]);

  const currentMonthActualNet = currentMonthNetPosition;
  const currentMonthRecurringCoverage = currentMonthRecurringExpectedTotal > 0
    ? currentMonthIncome.total / currentMonthRecurringExpectedTotal
    : 0;

  const currentMonthExpenseVsIncome = useMemo(() => ([
    {
      name: 'Income',
      value: currentMonthIncome.total
    },
    {
      name: 'Expenses',
      value: currentMonthActualOutflow
    }
  ]), [currentMonthActualOutflow, currentMonthIncome.total]);

  const yearIncomeExpenseTrend = useMemo(() => {
    const maxValue = { value: 1 };
    const trendRows = yearMonthKeys.map((monthKey) => {
      const schoolFees = schoolFeeIncome
        .filter((item) => getMonthKey(item.paymentDate || item.timeCreated) === monthKey)
        .reduce((sum, item) => sum + normalizeAmount(item.incomeAmount || item.amount), 0);

      const admission = admissionFees
        .filter((item) => getMonthKey(item.paymentDate || item.createdAt) === monthKey)
        .reduce((sum, item) => sum + normalizeAmount(item.amount || item.admissionFeeAmount), 0);

      const otherIncomeValue = otherIncome
        .filter((item) => getMonthKey(item.dateReceived || item.timeCreated) === monthKey)
        .reduce((sum, item) => sum + normalizeAmount(item.amount), 0);

      const totalIncome = schoolFees + admission + otherIncomeValue;

      const fixedBills = billPayments
        .filter((payment) => isSuccessfulFinancePayment(payment))
        .filter((payment) => resolveBillPaymentPeriodKey(payment) === monthKey)
        .reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);

      const requisitions = requisitionPayments
        .filter((payment) => resolveRequisitionPaymentPeriodKey(payment) === monthKey)
        .reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);

      const marketing = marketingExpenses
        .filter((item) => getMonthKey(item.expenseDate || item.createdAt || item.updatedAt) === monthKey)
        .reduce((sum, item) => sum + normalizeAmount(item.expenseAmount || item.amount), 0);

      const payroll = salaryPayments
        .filter((payment) => isSuccessfulFinancePayment(payment))
        .filter((payment) => resolveSalaryPaymentPeriodKey(payment) === monthKey)
        .reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);

      const liabilitiesPaid = liabilityPayments
        .filter((payment) => isSuccessfulFinancePayment(payment))
        .filter((payment) => getMonthKey(payment.paymentDate || payment.createdAt) === monthKey)
        .reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);

      const statutory = statutoryRows
        .filter((remittance) => remittance.periodKey === monthKey)
        .reduce((sum, remittance) => sum + normalizeAmount(remittance.paidAmount), 0);

      const legacy = legacyExpenses
        .filter((expense) => getMonthKey(expense.expenseDate || expense.timeStamp) === monthKey)
        .reduce((sum, expense) => sum + normalizeAmount(expense.expenseAmount), 0);

      const totalExpenses = fixedBills + requisitions + statutory + marketing + payroll + liabilitiesPaid + legacy;
      maxValue.value = Math.max(maxValue.value, totalIncome, totalExpenses);

      return {
        monthKey,
        monthLabel: getMonthShortLabel(monthKey),
        totalIncome,
        totalExpenses
      };
    });

    return trendRows.map((row) => ({
      ...row,
      incomeLine: row.totalIncome,
      expenseLine: row.totalExpenses,
      variance: row.totalIncome - row.totalExpenses,
      incomeWidth: `${Math.max((row.totalIncome / maxValue.value) * 100, 6)}%`,
      expenseWidth: `${Math.max((row.totalExpenses / maxValue.value) * 100, 6)}%`
    }));
  }, [
    admissionFees,
    billPayments,
    marketingExpenses,
    legacyExpenses,
    liabilityPayments,
    otherIncome,
    requisitionPayments,
    salaryPayments,
    schoolFeeIncome,
    statutoryRows,
    yearMonthKeys
  ]);

  const yearIncomeTotal = yearIncomeExpenseTrend.reduce((sum, row) => sum + normalizeAmount(row.totalIncome), 0);
  const yearExpenseTotal = yearIncomeExpenseTrend.reduce((sum, row) => sum + normalizeAmount(row.totalExpenses), 0);
  const yearVariance = yearIncomeTotal - yearExpenseTotal;
  const peakSpendMonth = [...yearIncomeExpenseTrend].sort((a, b) => b.totalExpenses - a.totalExpenses)[0] || null;
  const calmestSpendMonth = [...yearIncomeExpenseTrend].sort((a, b) => a.totalExpenses - b.totalExpenses)[0] || null;

  const coverageRatio = currentMonthRecurringCoverage;
  const netOperatingPosition = currentMonthActualNet;

  const riskTone = (() => {
    if (coverageRatio >= 1.2 && totalExposure <= currentMonthRecurringExpectedTotal * 2) {
      return 'success';
    }

    if (coverageRatio >= 0.95) {
      return 'warning';
    }

    return 'danger';
  })();

  const riskHeadline = (() => {
    if (coverageRatio >= 1.2 && totalExposure <= currentMonthRecurringExpectedTotal * 2) {
      return 'Healthy position';
    }

    if (coverageRatio >= 0.95) {
      return 'Watch closely';
    }

    return 'Pressure building';
  })();

  const refreshPage = async () => {
    try {
      setLoading(true);
      await loadReports();
    } catch (error) {
      console.error('Failed to refresh finance reports:', error);
      swal('Error', 'Finance reports could not be refreshed right now.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="financeReportsPage">
      <header className="financeReportsHeader">
        <div className="financeReportsHeroCopy">
          <span className="financeReportsEyebrow">Finance / Reports</span>
          <h1>Financial overview</h1>
          <p>
            Review income, spending, cashflow, and outstanding commitments.
          </p>
        </div>
      </header>

      <div className="appsFiltersCard financeReportsToolbar">
        <div className="appsFilterGroup financeReportsToolbarGroup">
          <Form.Control
            type="month"
            value={periodKey}
            onChange={(event) => setPeriodKey(event.target.value)}
            className="filterSelect financeReportsMonthInput"
          />
          <button type="button" className="opsGhostBtn financeReportsRefreshBtn" onClick={refreshPage}>
            <FontAwesomeIcon icon={faRotateRight} /> Refresh
          </button>
        </div>
        <div className="financeReportsToolbarMeta">
          <span>{currentMonthLabel}</span>
          <strong>{riskHeadline}</strong>
        </div>
      </div>

      {loading ? (
        <div className="opsLoadingCard financeReportsLoading">
          <Spinner animation="border" role="status" />
          <span>Loading finance reports...</span>
        </div>
      ) : (
        <>
          <div className="financeReportsStatsGrid">
            <article className="financeReportsStatCard">
              <span>Income this month</span>
              <strong>{formatKes(currentMonthIncome.total)}</strong>
              <small>School fees, admission fees, and other income.</small>
            </article>
            <article className="financeReportsStatCard">
              <span>Actual outflow</span>
              <strong>{formatKes(currentMonthActualOutflow)}</strong>
              <small>Recurring commitments plus current month variable spend.</small>
            </article>
            <article className="financeReportsStatCard">
              <span>Net cashflow</span>
              <strong>{formatKes(currentMonthActualNet)}</strong>
              <small>Income minus confirmed expenses for {currentMonthLabel}.</small>
            </article>
            <article className={`financeReportsStatCard ${riskTone}`}>
              <span>Open commitments</span>
              <strong>{formatKes(totalExposure)}</strong>
              <small>
                Bills, requisitions, payroll, and loans still open on the books.
              </small>
            </article>
          </div>

          <div className="financeReportsTopGrid">
            <section className="financeReportsCard financeReportsCard--chart">
              <div className="opsSectionHeader financeReportsSectionHeader">
                <div className="opsSectionIcon">
                  <FontAwesomeIcon icon={faChartLine} />
                </div>
                <div>
                  <h4>Income vs expenses for {selectedYear}</h4>
                  <p>Two lines, one glance: monthly income against monthly expenses across the year.</p>
                </div>
              </div>
              <div className="financeReportsChartWrap">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={yearIncomeExpenseTrend} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e8eef4" />
                    <XAxis dataKey="monthLabel" tickLine={false} axisLine={false} />
                    <YAxis tickLine={false} axisLine={false} tickFormatter={formatCompactKes} />
                    <Tooltip content={chartTooltip} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="incomeLine"
                      name="Income"
                      stroke="#111827"
                      strokeWidth={2.5}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="expenseLine"
                      name="Expenses"
                      stroke="#64748b"
                      strokeWidth={2.5}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="financeReportsChartFooter">
                <div className="financeReportsInsightPill">
                  <FontAwesomeIcon icon={faBullseye} />
                  <span>
                    This year income: <strong>{formatKes(yearIncomeTotal)}</strong>
                  </span>
                </div>
                <div className={`financeReportsInsightPill ${yearVariance >= 0 ? 'positive' : 'negative'}`}>
                  <FontAwesomeIcon icon={yearVariance >= 0 ? faShieldHalved : faTriangleExclamation} />
                  <span>
                    Year net: <strong>{formatKes(yearVariance)}</strong>
                  </span>
                </div>
                <div className="financeReportsInsightPill">
                  <FontAwesomeIcon icon={faCircleExclamation} />
                  <span>
                    This year expenses: <strong>{formatKes(yearExpenseTotal)}</strong>
                  </span>
                </div>
              </div>
            </section>
          </div>

          <div className="financeReportsBottomGrid financeReportsBottomGrid--threeUp">
            <section className="financeReportsCard financeReportsBreakdownCard">
              <div className="opsSectionHeader financeReportsSectionHeader">
                <div className="opsSectionIcon">
                  <FontAwesomeIcon icon={faPiggyBank} />
                </div>
                <div>
                  <h4>Fixed recurrent expenses</h4>
                  <p>These are the commitments you should budget for first.</p>
                </div>
              </div>

              <div className="financeReportsBreakdownList">
                {fixedRecurringBreakdown.map((item) => (
                  <div key={item.label} className="financeReportsBreakdownRow">
                    <div>
                      <strong>{item.label}</strong>
                      <span>{item.note}</span>
                    </div>
                    <div className="financeReportsBreakdownAmount">
                      <strong>{formatKes(item.amount)}</strong>
                    </div>
                  </div>
                ))}
              </div>

              <div className="financeReportsExposureFooter">
                <FontAwesomeIcon icon={faCoins} />
                <p>
                  Commitments total <strong>{formatKes(currentMonthRecurringExpectedTotal)}</strong>.
                  {' '}
                  Paid so far <strong>{formatKes(currentMonthRecurringPaidTotal)}</strong>.
                  {' '}
                  Outstanding <strong>{formatKes(currentMonthRecurringOutstanding)}</strong>.
                </p>
              </div>
            </section>

            <section className="financeReportsCard financeReportsBreakdownCard">
              <div className="opsSectionHeader financeReportsSectionHeader">
                <div className="opsSectionIcon">
                  <FontAwesomeIcon icon={faWallet} />
                </div>
                <div>
                  <h4>Current month spend</h4>
                  <p>These are the actual cash outflows outside the fixed recurrent bucket.</p>
                </div>
              </div>

              <div className="financeReportsBreakdownList">
                {currentExpenseBreakdown.map((item) => (
                  <div key={item.label} className="financeReportsBreakdownRow">
                    <div>
                      <strong>{item.label}</strong>
                      <span>{item.note}</span>
                    </div>
                    <div className="financeReportsBreakdownAmount">
                      <strong>{formatKes(item.amount)}</strong>
                    </div>
                  </div>
                ))}
              </div>

              <div className="financeReportsExposureFooter">
                <FontAwesomeIcon icon={faWallet} />
                <p>
                  Variable spend total <strong>{formatKes(currentMonthCurrentExpenseTotal)}</strong>.
                  {' '}
                  This excludes bills, payroll, and statutory items.
                </p>
              </div>
            </section>

            <aside className="financeReportsCard financeReportsChartCard">
              <div className="opsSectionHeader financeReportsSectionHeader">
                <div className="opsSectionIcon">
                  <FontAwesomeIcon icon={faHandHoldingDollar} />
                </div>
                <div>
                  <h4>Current month expense vs income</h4>
                  <p>A quick split of what came in versus what went out.</p>
                </div>
              </div>

              <div className="financeReportsDonutWrap">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={currentMonthExpenseVsIncome}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={68}
                      outerRadius={110}
                      paddingAngle={3}
                      stroke="none"
                    >
                      <Cell key="income" fill="#111827" />
                      <Cell key="expenses" fill="#94a3b8" />
                    </Pie>
                    <Tooltip formatter={(value) => formatKes(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="financeReportsExposureFooter financeReportsExposureFooter--chart">
                <p>
                  Current month income: <strong>{formatKes(currentMonthIncome.total)}</strong>.
                  {' '}
                  Current month expenses: <strong>{formatKes(currentMonthActualOutflow)}</strong>.
                </p>
              </div>
            </aside>
          </div>

          <div className="financeReportsBottomGrid">
            <section className="financeReportsCard">
              <div className="opsSectionHeader financeReportsSectionHeader">
                <div className="opsSectionIcon">
                  <FontAwesomeIcon icon={faHandHoldingDollar} />
                </div>
                <div>
                  <h4>Monthly position snapshot</h4>
                  <p>How income compares with the fixed and variable cash outflow this month.</p>
                </div>
              </div>

              <div className="financeReportsSummaryStrip">
                <div className="financeReportsSummaryItem">
                  <span>Collections</span>
                  <strong>{formatKes(currentMonthIncome.total)}</strong>
                </div>
                <div className="financeReportsSummaryItem">
                  <span>Recurring commitments</span>
                  <strong>{formatKes(currentMonthRecurringExpectedTotal)}</strong>
                </div>
                <div className="financeReportsSummaryItem">
                  <span>Current spend</span>
                  <strong>{formatKes(currentMonthCurrentExpenseTotal)}</strong>
                </div>
                <div className="financeReportsSummaryItem">
                  <span>Net position</span>
                  <strong>{formatKes(netOperatingPosition)}</strong>
                </div>
              </div>
              <div className="financeReportsSmallNote">
                <FontAwesomeIcon icon={faCoins} />
                <p>
                  Actual outflow this month: <strong>{formatKes(currentMonthActualOutflow)}</strong>.
                  {' '}
                  Coverage of recurring commitments: <strong>{coverageRatio.toFixed(2)}x</strong>.
                </p>
              </div>
            </section>

            <aside className="financeReportsCard">
              <div className="opsSectionHeader financeReportsSectionHeader">
                <div className="opsSectionIcon">
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <div>
                  <h4>Pressure points by category</h4>
                  <p>Quickly see where cash is still sitting open.</p>
                </div>
              </div>

              <div className="financeReportsCategoryList">
                <div className="financeReportsCategoryRow">
                  <span>Fixed bills</span>
                  <strong>{formatKes(liabilitySummary.fixedBills)}</strong>
                </div>
                <div className="financeReportsCategoryRow">
                  <span>Payroll</span>
                  <strong>{formatKes(liabilitySummary.payroll)}</strong>
                </div>
                <div className="financeReportsCategoryRow">
                  <span>Requisitions</span>
                  <strong>{formatKes(liabilitySummary.requisitions)}</strong>
                </div>
                <div className="financeReportsCategoryRow">
                  <span>Statutory remittances</span>
                  <strong>{formatKes(liabilitySummary.statutory)}</strong>
                </div>
                <div className="financeReportsCategoryRow">
                  <span>Liabilities</span>
                  <strong>{formatKes(liabilitySummary.loans)}</strong>
                </div>
              </div>

              <div className="financeReportsSmallNote">
                <FontAwesomeIcon icon={faCoins} />
                <p>
                  {peakSpendMonth
                    ? `Highest outflow month: ${peakSpendMonth.monthKey}. Lowest outflow month: ${calmestSpendMonth?.monthKey || peakSpendMonth.monthKey}.`
                    : 'No yearly spending pattern is available yet.'}
                </p>
              </div>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}

export default FinReportsComp;
